import { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import ConceptModal from "./ConceptModal";
import { Plus } from "react-bootstrap-icons";
import {
  humanizeCalcKind,
  CALC_KIND_BADGE_COLORS,
  humanizeName,
  humanizeUnit,
} from "../utils/rateConcepts";
import Swal from 'sweetalert2';

import api from "../api/axios";

export default function RateConceptsStep({ selectedConcepts, items, onChange }) {
  const [showModal, setShowModal] = useState(false);
  const [concepts, setConcepts] = useState([]);
  const [filteredConcepts, setFilteredConcepts] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  /* ===============================
    Cargar catálogo 
  =============================== */
  const loadCatalog = async () => {
    setLoading(true);
    try {
      const res = await api.get("/rate-concepts", {
        params: {
          search: search || undefined,
        },
      });

      setConcepts(res.data.data);
      setFilteredConcepts(res.data.data);
    } catch (err) {
      console.error("Error cargando conceptos:", err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadCatalog();
  }, []);

  const normalizeText = (text) => {
    return text
      ?.toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
  };

  // ==============================
  // Filtrar búsqueda
  // ==============================
  useEffect(() => {
    const q = normalizeText(search);

    // Si no hay búsqueda, mostrar todo
    if (!q) {
      setFilteredConcepts(concepts);
      return;
    }

    const result = concepts.filter((c) => {
      const kindKey = String(c.calc_kind ?? "").trim().toUpperCase();

      const fields = [
        // nombre (tal cual y humanizado)
        c.name,
        humanizeName(c),

        // tipo (técnico y humanizado)
        kindKey,
        humanizeCalcKind(kindKey),

        // unidad (técnico y humanizado)
        c.unit,
        humanizeUnit(c.unit),
      ];

      const haystack = normalizeText(fields.filter(Boolean).join(" | "));
      return haystack.includes(q);
    });

    setFilteredConcepts(result);
  }, [search, concepts]);

  const createConcept = async (payload) => {
    const res = await api.post("/rate-concepts", payload);
    return res.data.data; // ← concepto creado
  };

  const addConcept = (concept) => {
    if (selectedConcepts.some((c) => c.id === concept.id)) return;
    onChange([...selectedConcepts, concept]);
  };

  const removeConcept = async (conceptId) => {
    const hasRules = items.some(
      (item) =>
        item.concept_id === conceptId &&
        item.value_cents > 0
    );

    if (hasRules) {
      const result = await Swal.fire({
        title: "¿Desea continuar?",
        text: "Este concepto ya tiene reglas tarifarias configuradas.\n\n" +
              "Si lo elimina, estas reglas se perderán.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Sí, continuar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#0d6efd",   // Bootstrap primary
        cancelButtonColor: "#6c757d",    // Bootstrap secondary
        reverseButtons: true,
      });
  
      if (!result.isConfirmed) return;
    }

    onChange(
      selectedConcepts.filter((c) => c.id !== conceptId)
    );
  };

  const handleShowNew = () => {
    setShowModal(true);
  }


  return (
    <section className="card">
      <h2>Paso 2 — Conceptos tarifarios</h2>

      <p className="wizard-guide">
        Seleccione o cree los conceptos que componen esta tarifa.
        Cada concepto representa un tipo de cobro.
      </p>

      <div className="concepts-grid">
        {/* ===============================
            PANEL IZQUIERDO — CATÁLOGO
          =============================== */}
        <div className="panel">
          <div className="panel-header">
            <h3>Catálogo de conceptos</h3>
          </div>

          <div className="filters">
            <input
              type="text"
              placeholder="🔍 Buscar..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>

          <div className="table-scroll">
            <table className="wizard-table">
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Unidad</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <div className="p-3" style={{marginTop: '10%', width: '100%'}}>
                    <p>Cargando catálogo...</p>
                  </div>
                )}
                {filteredConcepts.map((c) => (
                  <tr key={c.id}>
                    <td>{humanizeName(c.name)}</td>
                    <td>
                      {(() => {
                        const kindKey = String(c.calc_kind ?? "").trim().toUpperCase();
                        return (
                          <span className={`badge ${CALC_KIND_BADGE_COLORS[kindKey] || "gray"}`}>
                            {humanizeCalcKind(kindKey) || "—"}
                          </span>
                        );
                      })()}
                    </td>

                    <td>{humanizeUnit(c.unit)}</td>
                    <td>
                      <button
                        className="btn-icon"
                        onClick={() => addConcept(c)}
                      >
                        <Plus />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <Button
            variant="outline-success"
            onClick={handleShowNew}
          >
            Nuevo concepto <Plus />
          </Button>
        </div>

        {/* ===============================
            PANEL DERECHO — SELECCIONADOS
          =============================== */}
        <div className="panel">
          <h3>Conceptos usados en esta tarifa</h3>

          {selectedConcepts.length === 0 && (
            <p className="muted">
              Aún no ha agregado conceptos.
            </p>
          )}

          {/* 👇 CONTENEDOR CON SCROLL */}
          <div className="concept-list-scroll">
            <div className="concept-list">
              {selectedConcepts.map((c) => (
                <div key={c.id} className="concept-card">
                  <div className="concept-card-header">
                    <strong>{humanizeName(c.name)}</strong>
                    <span
                      className={`badge ${CALC_KIND_BADGE_COLORS[c.calc_kind]}`}
                    >
                      {humanizeCalcKind(c.calc_kind)}
                    </span>
                  </div>

                  <div className="concept-summary">
                    {summaryByKind(c)}
                  </div>

                  <Button
                    variant="outline-danger"
                    style={{ width: "20%", padding: "0" }}
                    onClick={() => removeConcept(c.id)}
                  >
                    Quitar
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <ConceptModal
        show={showModal}
        onClose={() => setShowModal(false)}
        onCreate={createConcept}
        onSuccess={async (newConcept) => {
          setShowModal(false);
          addConcept(newConcept);
          await loadCatalog();
        }}
      />

    </section>
  );
}

export function summaryByKind(c) {
  switch (c.calc_kind) {
    case "BASE":
      return "Valor base del servicio.";

    case "INCREMENT_PER_UNIT":
      return `Incremento por cada ${
        c.unit_qty ?? "bloque"
      } ${humanizeUnit(c.unit).toLowerCase()}.`;

    case "ONE_TIME_SURCHARGE":
      return "Recargo único por evento.";

    case "HOURLY":
      return "Cobro por cada hora transcurrida.";

    case "MINIMUM_FARE":
      return "Asegura un valor mínimo del viaje.";

    default:
      return "";
  }
}


